/** Shared catalog filter query params (category pages, APIs, sidebar). */

export type CatalogQuery = {
  page?: number;
  business_ids?: string[];
  price_min?: number;
  price_max?: number;
  search?: string;
};

export function parsePositiveInt(raw: string | null | undefined): number | undefined {
  if (raw == null || raw.trim() === "") return undefined;
  const n = parseInt(raw.trim(), 10);
  if (!Number.isFinite(n) || n < 0) return undefined;
  return n;
}

export function parseBusinessIds(raw: string | null | undefined): string[] | undefined {
  if (raw == null || !raw.trim()) return undefined;
  const ids = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return ids.length > 0 ? ids : undefined;
}

export function parseCatalogSearchParams(
  searchParams: URLSearchParams | Record<string, string | string[] | undefined>,
): CatalogQuery {
  const get = (key: string): string | undefined => {
    if (searchParams instanceof URLSearchParams) {
      return searchParams.get(key) ?? undefined;
    }
    const v = searchParams[key];
    if (Array.isArray(v)) return v[0];
    return v;
  };

  const page = parsePositiveInt(get("page"));
  const price_min = parsePositiveInt(get("price_min"));
  const price_max = parsePositiveInt(get("price_max"));
  const business_ids = parseBusinessIds(get("business_ids"));
  const searchRaw = get("search");
  const search =
    typeof searchRaw === "string" && searchRaw.trim() ? searchRaw.trim() : undefined;

  return {
    ...(page != null && page >= 1 ? { page } : {}),
    ...(business_ids ? { business_ids } : {}),
    ...(price_min != null ? { price_min } : {}),
    ...(price_max != null ? { price_max } : {}),
    ...(search ? { search } : {}),
  };
}

export type CatalogFiltersQuery = CatalogQuery & { category?: string };

export function catalogQueryToSearchParams(
  query: CatalogFiltersQuery,
): URLSearchParams {
  const sp = new URLSearchParams();
  const category =
    typeof query.category === "string" ? query.category.trim() : "";
  if (category) sp.set("category", category);
  if (query.page != null && query.page > 1) {
    sp.set("page", String(query.page));
  }
  if (query.business_ids?.length) {
    sp.set("business_ids", query.business_ids.join(","));
  }
  if (query.price_min != null) sp.set("price_min", String(query.price_min));
  if (query.price_max != null) sp.set("price_max", String(query.price_max));
  if (query.search) sp.set("search", query.search);
  return sp;
}

export function categoryPageHref(slug: string, query: CatalogQuery = {}): string {
  const sp = catalogQueryToSearchParams(query);
  const qs = sp.toString();
  return qs ? `/category/${slug}?${qs}` : `/category/${slug}`;
}

export function searchPageHref(query: CatalogQuery = {}): string {
  const sp = catalogQueryToSearchParams(query);
  const qs = sp.toString();
  return qs ? `/search?${qs}` : "/search";
}

export function catalogFiltersApiQuery(query: CatalogFiltersQuery): string {
  return catalogQueryToSearchParams(query).toString();
}

/** App URLs use `price_min`/`price_max`; upstream webhooks expect `min_price`/`max_price`. */
export function appendUpstreamPriceParams(
  url: URL,
  query: Pick<CatalogQuery, "price_min" | "price_max">,
): void {
  if (query.price_min != null && Number.isFinite(query.price_min)) {
    url.searchParams.set("min_price", String(query.price_min));
  }
  if (query.price_max != null && Number.isFinite(query.price_max)) {
    url.searchParams.set("max_price", String(query.price_max));
  }
}
